
import React from "react";
import { Modal, Badge } from "../../../components/ui";
import { fmtDate, fmt } from "../../../lib/formatters";
import { Package, Truck, User, ArrowLeftCircle, CheckCircle, FileText, Calendar, DollarSign, AlertTriangle } from "lucide-react";

export const MaterialReturnDetailModal = ({ selReturn, onClose, user, onConfirmReceipt }) => {
  if (!selReturn) return null;

  const isInventoryManager =
    user?.role === "inventory_manager" ||
    user?.role === "store-manager" ||
    user?.role === "admin" ||
    user?.role === "solar-admin" ||
    user?.role === "owner";

  const data = selReturn.originalData || selReturn;

  return (
    <Modal
      isOpen={!!selReturn}
      onClose={onClose}
      title="Material Return Details"
    >
      <div className="space-y-6">
        {/* Header Information */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-4">
             <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center border border-slate-100">
                <ArrowLeftCircle size={24} />
             </div>
             <div>
                <h3 className="text-[16px] font-semibold text-slate-800 font-['Poppins']">Site Return Request</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[12px] font-medium text-slate-500">
                    ID: {data.returnId || `RET-${(data._id || '').toString().slice(-6).toUpperCase()}`}
                  </span>
                  <span className="text-slate-300">•</span>
                  <span className="text-[11px] font-medium text-slate-400">
                    Created {fmtDate(data.createdAt)}
                  </span>
                </div>
             </div>
          </div>
          <Badge status={data.status || 'PENDING'} />
        </div>

        {/* Return Information Grid */}
        <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
              <div className="flex items-center gap-2 text-slate-400 mb-1">
                <Calendar size={14} />
                <span className="text-[11px] font-semibold uppercase tracking-wider">Return Info</span>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Return Date</p>
                <p className="text-[13px] font-medium text-slate-700 font-['Poppins']">{fmtDate(data.returnDate || data.date) || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Reason</p>
                <p className="text-[13px] font-medium text-slate-700 font-['Poppins']">{data.reason || data.notes || data.remarks || 'General Return'}</p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
              <div className="flex items-center gap-2 text-slate-400 mb-1">
                <DollarSign size={14} />
                <span className="text-[11px] font-semibold uppercase tracking-wider">Financials</span>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Estimated Value</p>
                <p className="text-[13px] font-semibold text-[#2b448f] font-['Poppins']">
                  {fmt(data.totalAmount || data.amount || (data.items || []).reduce((sum, it) => {
                    const item = it.inventoryId?.item || it.item || {};
                    const rate = it.inventoryId?.purchaseRate || it.inventoryId?.unitPrice || item.purchaseAmount || item.unitPrice || it.unitPrice || it.rate || 0;
                    return sum + (rate * (it.quantity || it.qty || 0));
                  }, 0))}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Total Items</p>
                <p className="text-[13px] font-medium text-slate-700 font-['Poppins']">{data.items?.length || 0} items</p>
              </div>
            </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
           <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
              <div className="flex items-center gap-2 text-slate-400 mb-1">
                <Truck size={14} />
                <span className="text-[11px] font-semibold uppercase tracking-wider">Logistics</span>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Driver / Vehicle</p>
                <p className="text-[13px] font-medium text-slate-700 font-['Poppins']">
                  {data.driverName || '—'} {data.vehicleNumber ? `(${data.vehicleNumber})` : ''}
                </p>
              </div>
           </div>

           <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
              <div className="flex items-center gap-2 text-slate-400 mb-1">
                <User size={14} />
                <span className="text-[11px] font-semibold uppercase tracking-wider">Reporting</span>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Project ID</p>
                <p className="text-[13px] font-semibold text-[#f97316] font-['Poppins']">
                  {data.project?.projectId || data.projectId || 'N/A'}
                </p>
              </div>
           </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-[14px] font-semibold text-slate-800 flex items-center gap-2 font-['Poppins']">
            <Package size={18} className="text-slate-400" />
            Returned Materials
          </h4>
          <div className="border border-slate-100 rounded-xl overflow-hidden bg-white shadow-sm">
             <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-4 py-2.5 text-[13px] font-semibold text-slate-500 uppercase">Item Details</th>
                    <th className="px-4 py-2.5 text-[13px] font-semibold text-slate-500 uppercase text-center">Qty</th>
                    <th className="px-4 py-2.5 text-[13px] font-semibold text-slate-500 uppercase text-right">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {data.items?.map((it, idx) => {
                      const item = it.inventoryId?.item || it.item || {};
                      const name = item.itemName || item.name || it.itemName || it.name || 'Unknown Item';
                      const sku = item.sku || item.itemCode || it.itemCode || 'N/A';
                      // Robust price lookup: check inventory record rate, item master price, or return entry rate
                      const unitPrice = it.inventoryId?.purchaseRate || it.inventoryId?.unitPrice || item.purchaseAmount || item.unitPrice || it.unitPrice || it.rate || 0;
                      const totalValue = unitPrice * (it.quantity || it.qty || 0);
                      
                      return (
                       <tr key={idx} className="hover:bg-slate-50/50">
                        <td className="px-4 py-3">
                           <div>
                             <p className="text-[13px] font-medium text-slate-700 font-['Poppins']">{name}</p>
                             <p className="text-[10px] text-slate-400 uppercase tracking-tight">{sku}</p>
                           </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                           <span className="text-[13px] font-medium text-slate-600">{it.quantity || it.qty || 0}</span>
                           <span className="ml-1 text-[10px] text-slate-400 uppercase">{item.units?.unitName || item.unit || it.unit || 'pcs'}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                           <span className="text-[13px] font-semibold text-[#2b448f] font-['Poppins']">{fmt(totalValue)}</span>
                        </td>
                      </tr>
                     );
                   })}
                </tbody>
             </table>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 pt-4 border-t border-slate-100">
           <button 
             className="flex-1 px-4 py-2.5 bg-slate-50 text-slate-600 font-medium text-[13px] rounded-lg border border-slate-200 hover:bg-slate-100 transition-all" 
             onClick={onClose}
           >
             Close
           </button>
           {data.status !== 'RECEIVED' && data.status !== 'RESTOCKED' && isInventoryManager && (
             <button 
               className="flex-[2] px-4 py-2.5 bg-[#f97316] text-white font-semibold text-[13px] rounded-lg shadow-sm hover:bg-[#ea580c] transition-all flex items-center justify-center gap-2"
               onClick={() => {
                 if (window.confirm("Confirm receipt of these materials and add them back to inventory stock?")) {
                    onConfirmReceipt(String(data._id || data.id));
                 }
               }}
             >
                <CheckCircle size={18} /> Confirm Receipt
             </button>
           )}
        </div>
      </div>
    </Modal>
  );
};


